import type { CSSProperties } from "react";
import type { Actor } from "../models/Actor";

export const ACTOR_AVATAR_PALETTE: string[] = [
  "bg-creator-primary text-white",
  "bg-creator-secondary text-white",
  "bg-creator-error text-white",
  "bg-interaction-secondary text-white",
  "bg-interaction-correct text-white",
  "bg-creator-text-soft text-white",
  // was game blue (bg-interaction-primary): it vanished on the pale blue popup backgrounds
  "bg-[var(--pi-purple)] text-white",
  "bg-interaction-false text-white",
];

function hashString(seed: string): number {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function normalizeAvatarColorIndex(index: number): number {
  if (!Number.isFinite(index)) return 0;
  return (
    ((Math.trunc(index) % ACTOR_AVATAR_PALETTE.length) +
      ACTOR_AVATAR_PALETTE.length) %
    ACTOR_AVATAR_PALETTE.length
  );
}

export function getActorAvatarColorByIndex(index: number): string {
  return ACTOR_AVATAR_PALETTE[normalizeAvatarColorIndex(index)];
}

export function getActorAvatarColorIndex(
  actor: Pick<Actor, "id" | "profilePicture"> | undefined
): number {
  if (!actor) return 0;
  if (typeof actor.profilePicture === "number" && actor.profilePicture >= 0) {
    return normalizeAvatarColorIndex(actor.profilePicture);
  }
  return hashString(actor.id ?? "") % ACTOR_AVATAR_PALETTE.length;
}

export function getActorAvatarColor(
  actor: Pick<Actor, "id" | "profilePicture"> | undefined
): string {
  return ACTOR_AVATAR_PALETTE[getActorAvatarColorIndex(actor)];
}

export function getActorAvatarInitial(workingName: string | undefined): string {
  const trimmed = (workingName ?? "").trim();
  return (trimmed[0] ?? "?").toUpperCase();
}

/**
 * The palette as plain CSS colours (same order as ACTOR_AVATAR_PALETTE), for the
 * soft avatar: a light tint of the colour with the initial in a darker shade.
 */
export const ACTOR_AVATAR_COLORS: string[] = [
  "var(--pi-key-blue)",
  "var(--pi-yellow)",
  "var(--pi-error)",
  "var(--pi-game-orange)",
  "var(--pi-game-green)",
  "#919BA7",
  "var(--pi-purple)",
  "#D35F5F",
];

/** The actor's identity colour; the Player is always Creator blue. */
export function getActorAvatarCssColor(
  actor: Pick<Actor, "id" | "profilePicture"> | undefined
): string {
  if (actor?.id === "player") return "var(--pi-key-blue)";
  return ACTOR_AVATAR_COLORS[getActorAvatarColorIndex(actor)];
}

/** Soft avatar colours: tinted fill, initial in the colour darkened towards the text colour (readable in light and dark). */
export function getActorAvatarSoftStyle(color: string, emphasised = false): CSSProperties {
  return {
    backgroundColor: `color-mix(in srgb, ${color} ${emphasised ? 26 : 16}%, var(--pi-surface))`,
    color: `color-mix(in srgb, ${color} 58%, var(--pi-text-color-1))`,
  };
}
